import React from 'react';
import i18n from '../../../../../i18n';
import { fmt } from '../../../data/metrics';
import type { DrillSellAttach } from '../../../types';
import { DrillToolbarStat } from '../../shared/DrillToolbar';
import { DrillCruxStat } from './DrillCruxStat';
import { DrillTile } from './DrillTile';
import { countLabel, signed } from './substanceFormat';

const T = 'performance:drillSell.attach';

export function DrillSellAttachSection({
  data,
  fmtMoney,
  compareLabel,
}: {
  metricKey: string;
  data: DrillSellAttach;
  /** Same money format as the sell-items hero. */
  fmtMoney: (v: number) => string;
  /** Compare period label; deltas show only with one. */
  compareLabel?: string;
}) {
  const cmp = compareLabel ? data.compare : undefined;
  const attachDelta =
    cmp && data.attachDeltaPt != null
      ? `${i18n.t('performance:drillUi.ptDelta', {
          delta: signed(data.attachDeltaPt),
        })} · ${i18n.t(`${T}.was`, {
          value: `${cmp.attachRatePct}%`,
          label: compareLabel,
        })}`
      : null;
  const perGuestDelta =
    cmp && data.revenuePerGuestDeltaPct != null
      ? `${signed(data.revenuePerGuestDeltaPct)}% · ${i18n.t(`${T}.was`, {
          value: fmtMoney(cmp.revenuePerGuest),
          label: compareLabel,
        })}`
      : null;
  return (
    <DrillTile
      sectionId="sell-attach"
      span={4}
      title={i18n.t(`${T}.title`)}
      meta={countLabel('performance:drillSell.mix.reservations', data.servedReservations)}
    >
      <div className="dcrux-stat-row">
        <div>
          <DrillCruxStat
            value={`${data.attachRatePct}%`}
            label={i18n.t(`${T}.attachRate`)}
            delta={attachDelta}
            deltaGood={
              data.attachDeltaPt != null ? data.attachDeltaPt >= 0 : undefined
            }
          />
          <div className="dsub-n">
            {i18n.t(`${T}.attachN`, {
              pre: fmt.num(data.preorderReservations),
              served: fmt.num(data.servedReservations),
            })}
          </div>
        </div>
        <div>
          <DrillCruxStat
            value={fmtMoney(data.revenuePerGuest)}
            label={i18n.t(`${T}.perGuest`)}
            delta={perGuestDelta}
            deltaGood={
              data.revenuePerGuestDeltaPct != null
                ? data.revenuePerGuestDeltaPct >= 0
                : undefined
            }
          />
          <div className="dsub-n">
            {i18n.t(`${T}.perGuestN`, {
              guests: fmt.num(data.preorderGuests),
              revenue: fmtMoney(data.preorderRevenue),
            })}
          </div>
        </div>
      </div>
      <p className="ddrill-tile-note">{i18n.t(`${T}.basis`)}</p>
    </DrillTile>
  );
}

function HeroDelta({ text, good }: { text: string; good: boolean | null }) {
  return (
    <div
      className="drill-hero-d drill-toolbar-trend"
      style={{
        color:
          good == null ? 'var(--ink-3)' : good ? 'var(--st-steady)' : 'var(--st-alert)',
      }}
    >
      {text}
    </div>
  );
}

/** Attach rate and revenue per pre-ordering guest beside the sell-items hero. */
export function SellAttachHeroStats({
  data,
  fmtMoney,
  trendLabel,
}: {
  data: DrillSellAttach;
  fmtMoney: (v: number) => string;
  /** "vs …" label of the hero trend; deltas show only with one. */
  trendLabel?: string;
}) {
  const withCmp = !!trendLabel && !!data.compare;
  const attachN = `n = ${fmt.num(data.servedReservations)}`;
  const guestsN = `n = ${fmt.num(data.preorderGuests)}`;
  const attachText =
    withCmp && data.attachDeltaPt != null
      ? `${attachN} · ${i18n.t('performance:drillUi.ptDelta', {
          delta: signed(data.attachDeltaPt),
        })} · ${trendLabel}`
      : attachN;
  const perGuestText =
    withCmp && data.revenuePerGuestDeltaPct != null
      ? `${guestsN} · ${signed(data.revenuePerGuestDeltaPct)}% · ${trendLabel}`
      : guestsN;
  return (
    <>
      <DrillToolbarStat
        label={i18n.t(`${T}.attachRate`)}
        value={`${data.attachRatePct}%`}
        secondary
        trend={
          <HeroDelta
            text={attachText}
            good={withCmp && data.attachDeltaPt != null ? data.attachDeltaPt >= 0 : null}
          />
        }
      />
      <DrillToolbarStat
        label={i18n.t(`${T}.perGuest`)}
        value={fmtMoney(data.revenuePerGuest)}
        secondary
        trend={
          <HeroDelta
            text={perGuestText}
            good={
              withCmp && data.revenuePerGuestDeltaPct != null
                ? data.revenuePerGuestDeltaPct >= 0
                : null
            }
          />
        }
      />
    </>
  );
}
